import type { KeyboardEvent, SyntheticEvent } from "react";

import { AlertTriangle, MarkerPin01 } from "@untitledui/icons";

import { Badge } from "@/components/ui/badges/badges";
import { SearchInputWithClear } from "@/components/ui/input/search-input-with-clear";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip/tooltip";
import { cx } from "@/utils/cx";

import type { ShadowLocation } from "../../../types/location-map";

import {
	aggregateSensorStatusCounts,
	buildClusterStatusSegments,
} from "../../../map/markers/aggregate-status";
import { isLocationMappable } from "../../../utils/location/location-map-features";
import {
	formatLocationSetupGapsTooltip,
	getLocationSetupGaps,
	hasStructuralSetupGaps,
} from "../../../utils/location/location-setup-gaps";
import { toTitleCase } from "../../../utils/shared/strings";
import { MapEmptyState } from "../map-pane/map-empty-state";

interface LocationsSidebarListProps {
	hoveredLocationId?: string;
	/** Location ids that already have at least one installation. */
	locationIdsWithInstallation?: ReadonlySet<string>;
	locations: ShadowLocation[];
	onHoverLocation?: (locationId: string) => void;
	onHoverLocationEnd?: () => void;
	onSelectLocation: (locationId: string) => void;
	searchQuery: string;
	setSearchQuery: (value: string) => void;
}

const locationSensors = (location: ShadowLocation) =>
	(location.zones ?? []).flatMap((zone) => zone.sensors ?? []);

const stopRowActivate = (event: SyntheticEvent) => {
	event.stopPropagation();
};

export const LocationsSidebarList = ({
	hoveredLocationId,
	locationIdsWithInstallation,
	locations,
	onHoverLocation,
	onHoverLocationEnd,
	onSelectLocation,
	searchQuery,
	setSearchQuery,
}: LocationsSidebarListProps) => {
	const trimmedQuery = searchQuery.trim().toLowerCase();
	const filteredLocations = trimmedQuery
		? locations.filter((location) =>
				location.name.toLowerCase().includes(trimmedQuery),
			)
		: locations;

	return (
		<div className="flex min-h-0 flex-1 flex-col overflow-hidden">
			<div className="shrink-0 px-4 pb-3 md:px-6">
				<SearchInputWithClear
					aria-label="Search locations"
					className="w-full"
					onChange={setSearchQuery}
					placeholder="Search locations..."
					size="sm"
					value={searchQuery}
				/>
			</div>

			{filteredLocations.length === 0 ? (
				<MapEmptyState
					className="flex-1"
					description={
						trimmedQuery
							? `No locations match “${searchQuery.trim()}”.`
							: "This organization has no locations yet."
					}
					title={trimmedQuery ? "No Location Results" : "No Locations"}
				/>
			) : (
				<ul className="min-h-0 flex-1 list-none divide-y divide-secondary overflow-y-auto overscroll-contain">
					{filteredLocations.map((location) => {
						const mappable = isLocationMappable(location);
						const isHovered = hoveredLocationId === location.id;
						const sensors = locationSensors(location);
						const statusCounts = aggregateSensorStatusCounts(sensors);
						const missingSetup = getLocationSetupGaps(
							location,
							locationIdsWithInstallation?.has(location.id) ?? false,
						);
						const segments = buildClusterStatusSegments({
							...statusCounts,
							missingSetup,
							surface: "light",
						});
						const showSetupAlert = hasStructuralSetupGaps(missingSetup);
						const missingTooltip = showSetupAlert
							? formatLocationSetupGapsTooltip(missingSetup)
							: "";
						const sensorCount = sensors.length;
						const sensorCountLabel = `${sensorCount} ${sensorCount === 1 ? "sensor" : "sensors"}`;

						const handleActivate = () => onSelectLocation(location.id);
						const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
							if (event.key === "Enter" || event.key === " ") {
								event.preventDefault();
								handleActivate();
							}
						};

						return (
							<li className="mx-4 md:mx-6" key={location.id}>
								{/* `role="button"` (not <button>) so the setup-gap tooltip
								    trigger can remain a real button without invalid nesting. */}
								<div
									className={cx(
										"flex w-full items-center justify-between gap-3 border-l-2 py-3 pr-3 pl-4 text-left transition-colors",
										"outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-[-2px]",
										isHovered
											? "border-brand bg-brand-primary_alt"
											: "hover:bg-secondary_subtle border-transparent",
									)}
									onClick={handleActivate}
									onKeyDown={handleKeyDown}
									onMouseEnter={() => onHoverLocation?.(location.id)}
									onMouseLeave={() => onHoverLocationEnd?.()}
									role="button"
									tabIndex={0}
								>
									<div className="flex min-w-0 flex-1 items-center gap-2">
										<MarkerPin01
											aria-hidden
											className={cx(
												"size-4 shrink-0",
												isHovered
													? "text-brand-secondary"
													: "text-fg-quaternary",
											)}
										/>
										<div className="min-w-0 flex-1">
											<p className="text-[11px] leading-none font-medium tracking-wide text-tertiary uppercase">
												Location
											</p>
											<p
												className={cx(
													"mt-0.5 truncate text-sm font-semibold",
													isHovered ? "text-brand-secondary" : "text-primary",
												)}
											>
												{toTitleCase(location.name)}
											</p>
											{segments.length > 0 ? (
												<p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs leading-tight font-medium">
													{segments.map((segment) => (
														<span
															className={segment.className}
															key={segment.kind}
														>
															{segment.text}
														</span>
													))}
												</p>
											) : null}
										</div>
										{missingTooltip ? (
											<Tooltip placement="top" title={missingTooltip}>
												<TooltipTrigger
													aria-label={missingTooltip}
													className="inline-flex shrink-0"
													onClick={stopRowActivate}
													onPointerDown={stopRowActivate}
												>
													<AlertTriangle className="size-4 shrink-0 text-utility-warning-500" />
												</TooltipTrigger>
											</Tooltip>
										) : null}
									</div>
									<div className="flex shrink-0 items-center gap-2">
										{mappable ? null : (
											<Badge color="gray" size="sm" type="pill-color">
												No map position
											</Badge>
										)}
										<Badge color="gray" size="sm" type="pill-color">
											{sensorCountLabel}
										</Badge>
									</div>
								</div>
							</li>
						);
					})}
				</ul>
			)}
		</div>
	);
};
